import { Link } from "@tanstack/react-router";
import { Store } from "lucide-react";

import { Img, Price, fenInt } from "../ui.tsx";
import { readPurchaseAs, readViewAs } from "../../lib/identity.ts";

/**
 * 网页版的商品卡。
 *
 * 🔴 **跟手机版是两张不同的卡。**
 *    手机上是瀑布流（图片高度不一，靠错落感填满两列窄栏）；
 *    网页上是**等高网格** —— 一行四张并排，高度不齐会像没对齐的货架，
 *    而并排比较正是网页版的价值所在。
 *
 * ⚠️ 所以图统一 4:3、标题固定两行、价格永远在卡片底部同一条水平线上。
 */

export interface DesktopCardItem {
  id: number;
  title: string;
  /** ⚠️ 列表接口给的是 `imageUrls` 数组（服务端转好的地址），取第一张当主图。 */
  imageUrls: string[];
  fromPrice: number;
  listPrice?: number | null;
  salesCount: number;
}

export function DesktopProductCard({
  p,
  shop,
  myShopName,
}: {
  p: DesktopCardItem;
  shop?: { id: number; name: string } | null;
  myShopName?: string | null;
}) {
  const orgId = readPurchaseAs();
  const previewing = orgId !== null && readViewAs() === "shop";

  return (
    <Link
      to="/products/$id"
      params={{ id: String(p.id) }}
      search={(shop ? { shopId: shop.id } : {}) as never}
      className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-brand-200 hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)]"
    >
      {/* ⚠️ 统一 4:3 并 `overflow-hidden`，hover 时图轻微放大 —— 网页上鼠标要有反馈。 */}
      <div className="aspect-[4/3] overflow-hidden bg-secondary">
        <Img
          src={p.imageUrls[0]}
          alt={p.title}
          className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      </div>

      <div className="flex min-h-[7.5rem] flex-1 flex-col p-4">
        {/* ⚠️ 固定两行：一行和三行的卡混在一起，价格就不在同一条线上了。 */}
        <p className="line-clamp-2 min-h-[2.75rem] text-[15px] leading-snug">{p.title}</p>

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {shop && (
            <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <Store className="size-3" />
              {shop.name}
            </span>
          )}
          {!shop && myShopName && (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700">
              你的专属价
            </span>
          )}
          {/*
            🔴 **不在每张卡上标「进货价」。** 老板：「进货价这些内容不要显示」。
               一屏三十件商品就是三十个一模一样的角标 —— 而"现在看到的是哪个价"
               页面顶部那条横幅（`PurchaseBanner`）已经说过一次了，
               每张卡再标一遍纯属噪音。
            ⚠️ 「你的专属价」留着：那不是在解释当前是什么模式，
               是在告诉客户"挂在这家店名下比外面便宜"，一句都没有别的地方说。
          */}
          {!shop && !myShopName && previewing && (
            <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[11px] text-sky-800">
              客户看到的价
            </span>
          )}
          {/*
            🔴 **游客不标「平台指导价」** —— 那个词在向客户解释平台的定价机制，
               而且暗示"你现在看到的是最贵的一档"。商家侧的「进货价」
               「客户看到的价」保留，那是给他分辨自己在看哪一档的。
          */}
        </div>

        {/* ⚠️ `mt-auto` 把价格压到卡片底部 —— 这就是"同一条水平线"的实现。 */}
        <div className="mt-auto flex items-end justify-between gap-2 pt-2.5">
          <span className="min-w-0">
            <Price cents={p.fromPrice} className="text-[19px] text-brand-600" />
            <span className="ml-0.5 text-[12px] text-muted-foreground">起</span>
            {p.listPrice != null && p.listPrice > p.fromPrice && (
              <span className="ml-2 text-[12px] text-muted-foreground line-through">
                {fenInt(p.listPrice)}
              </span>
            )}
          </span>
          {p.salesCount > 0 && (
            <span className="shrink-0 text-[12px] text-muted-foreground tabular-nums">
              {p.salesCount} 人买过
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
